// The bots-first sections of the home page. Words are in landing.json (home.*);
// links to the bot, use-case, integration and comparison pages come from
// data/guides.js with their labels in landing.json (guide_links.*).
import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight, Bot, User, MessageSquare, ListChecks, Monitor, Brain, CalendarClock, Plug, ShieldCheck, Cpu, Check,
  LayoutDashboard, CalendarDays, FileText, Focus, Repeat, Database, Network, Users, Smartphone, Globe,
} from 'lucide-react';
import LandingFigure from './LandingFigure';
import { useAuth } from '../contexts/AuthContext';
import { displayCurrency, formatMoney, usePlanCatalog } from '../data/pricing';
import { BOT_GUIDES, APP_GUIDES, COMPARE_GUIDES, DOWNLOAD_GUIDES } from '../data/guides';

const ICONS = {
  'message-square': MessageSquare, 'list-checks': ListChecks, monitor: Monitor, brain: Brain,
  'calendar-clock': CalendarClock, plug: Plug, 'shield-check': ShieldCheck, cpu: Cpu, bot: Bot,
  smartphone: Smartphone,
};
const label = (path) => `guide_links.${path.slice(1)}`;

const Head = ({ kicker, title, subtitle }) => (
  <div className="text-center max-w-3xl mx-auto mb-14">
    {kicker && <p className="text-amber-400 font-medium mb-3">{kicker}</p>}
    <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 tracking-tight">{title}</h2>
    {subtitle && <p className="text-lg md:text-xl text-gray-400 leading-relaxed">{subtitle}</p>}
  </div>
);

// The work app the bots live in: three pillars with real screens, then the rest
// of the app as links (their names are the menu's, nav.items.*).
const PILLARS = [
  { key: 'tasks', img: 'board', path: '/features/projects', Icon: LayoutDashboard },
  { key: 'planner', img: 'planner', path: '/features/calendar', Icon: CalendarDays },
  { key: 'docs', img: 'whiteboard', path: '/features/documents', Icon: FileText },
];
const MORE = [
  { key: 'databases', path: '/features/databases', Icon: Database },
  { key: 'mindmaps', path: '/features/mindmaps', Icon: Network },
  { key: 'focus_time', path: '/features/focus-time', Icon: Focus },
  { key: 'habits', path: '/features/habits', Icon: Repeat },
  { key: 'team_collaboration', path: '/features/team-collaboration', Icon: Users },
  { key: 'desktop_app', path: '/download/desktop', Icon: Monitor },
  { key: 'mobile_app', path: '/download/mobile', Icon: Smartphone },
];

export const WorkApp = () => {
  const { t } = useTranslation('landing');
  return (
    <section id="work-app" className="py-24 bg-gradient-to-b from-[#0a0a0b] to-[#0f0f12] scroll-mt-20">
      <div className="max-w-7xl mx-auto px-4">
        <Head kicker={t('home.work_app.kicker')} title={t('home.work_app.title')} subtitle={t('home.work_app.subtitle')} />
        <div className="grid lg:grid-cols-3 gap-6">
          {PILLARS.map(({ key, img, path, Icon }) => (
            <div key={key} className="flex flex-col rounded-2xl border border-gray-800 bg-white/[0.03] overflow-hidden">
              <LandingFigure src={`/screenshots/work-app/${img}-card.webp`} width={1280} height={800}
                alt={t(`home.work_app.items.${key}.image_alt`)} />
              <div className="p-6 flex flex-col flex-1">
                <h3 className="text-white font-semibold text-lg mb-2 flex items-center gap-2">
                  <Icon className="w-5 h-5 text-amber-400" />{t(`home.work_app.items.${key}.t`)}
                </h3>
                <p className="text-gray-400 leading-relaxed text-sm flex-1">{t(`home.work_app.items.${key}.d`)}</p>
                <div className="mt-5 flex items-center justify-between gap-3">
                  <span className="text-xs text-gray-500">{t(`home.work_app.items.${key}.like`)}</span>
                  <Link to={path} aria-label={t(`home.work_app.items.${key}.t`)}
                    className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-gray-700 text-amber-400 hover:border-amber-500/60 hover:text-amber-300 transition-colors shrink-0">
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-10">
          <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-3 text-center">{t('home.work_app.more_title')}</div>
          <div className="flex flex-wrap justify-center gap-2">
            {MORE.map(({ key, path, Icon }) => (
              <Link key={key} to={path} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gray-800 bg-white/[0.03] text-sm text-gray-300 hover:text-white hover:border-amber-500/40 transition-colors">
                <Icon className="w-4 h-4 text-amber-400" />{t(`nav.items.${key}.name`)}
              </Link>
            ))}
          </div>
        </div>
        <p className="text-center text-sm text-gray-500 mt-8">{t('home.work_app.note')}</p>
      </div>
    </section>
  );
};

// Six hand-offs, each tied to the freelancer page it comes from.
const HANDOFFS = [
  { key: 'designer', path: '/use-cases/designers' },
  { key: 'developer', path: '/use-cases/developers' },
  { key: 'marketer', path: '/use-cases/marketers' },
  { key: 'writer', path: '/use-cases/writers' },
  { key: 'consultant', path: '/use-cases/consultants' },
  { key: 'founder', path: '/use-cases/solo-founders' },
];

export const BotHandoffs = () => {
  const { t } = useTranslation('landing');
  return (
    <section className="py-24 bg-[#0a0a0b]">
      <div className="max-w-7xl mx-auto px-4">
        <Head kicker={t('home.handoffs.kicker')} title={t('home.handoffs.title')} subtitle={t('home.handoffs.subtitle')} />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {HANDOFFS.map(({ key, path }) => (
            <Link key={key} to={path} className="group flex flex-col rounded-2xl border border-gray-800 bg-white/[0.03] hover:border-amber-500/40 transition-colors overflow-hidden">
              <div className="p-5 border-b border-gray-800">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2">
                  <User className="w-3.5 h-3.5" />{t(`home.handoffs.items.${key}.role`)}
                </div>
                <p className="text-white leading-relaxed">{t(`home.handoffs.items.${key}.you`)}</p>
              </div>
              <div className="p-5 flex-1 bg-amber-500/[0.04]">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-amber-400 font-semibold mb-2">
                  <Bot className="w-3.5 h-3.5" />{t('home.handoffs.bot_label')}
                </div>
                <p className="text-gray-300 leading-relaxed text-sm">{t(`home.handoffs.items.${key}.bot`)}</p>
              </div>
            </Link>
          ))}
        </div>
        <div className="text-center mt-10">
          <Link to="/use-cases" className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 font-medium">
            {t('home.handoffs.more')}<ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

// One input, BusyBee hands the work on, the bot keeps going on its own, you answer only what needs you.
const STEPS = ['ask', 'handoff', 'work', 'review'];

export const BotSteps = () => {
  const { t } = useTranslation('landing');
  return (
    // The hero's "Watch a bee work" scrolls here (Hero.jsx).
    <section id="how-it-works" className="py-24 bg-gradient-to-b from-[#0a0a0b] to-[#0f0f12] scroll-mt-20">
      <div className="max-w-7xl mx-auto px-4">
        <Head kicker={t('home.steps.kicker')} title={t('home.steps.title')} subtitle={t('home.steps.subtitle')} />
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <ol className="lg:col-span-5 space-y-6">
            {STEPS.map((key, i) => (
              <li key={key} className="flex gap-4">
                <span className="w-9 h-9 flex-shrink-0 rounded-full bg-amber-500/20 border border-amber-500/30 text-amber-400 flex items-center justify-center text-sm font-bold">{i + 1}</span>
                <div>
                  <h3 className="text-white font-semibold text-lg mb-1">{t(`home.steps.items.${key}.t`)}</h3>
                  <p className="text-gray-400 leading-relaxed">{t(`home.steps.items.${key}.d`)}</p>
                </div>
              </li>
            ))}
          </ol>
          <div className="lg:col-span-7">
            <LandingFigure src="/screenshots/home/needs-you.webp" alt={t('home.steps.image_alt')} caption={t('home.steps.image_caption')} />
          </div>
        </div>
      </div>
    </section>
  );
};

export const BotCapabilities = () => {
  const { t } = useTranslation('landing');
  return (
    <section className="py-24 bg-[#0f0f12]">
      <div className="max-w-7xl mx-auto px-4">
        <Head kicker={t('home.capabilities.kicker')} title={t('home.capabilities.title')} subtitle={t('home.capabilities.subtitle')} />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {BOT_GUIDES.map((g) => {
            const Icon = ICONS[g.icon] || Bot;
            return (
              <Link key={g.path} to={g.path} className="group p-6 rounded-2xl bg-white/5 border border-gray-800 hover:border-amber-500/40 transition-colors">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-amber-400" />
                </div>
                <h3 className="text-white font-semibold mb-2 group-hover:text-amber-400 transition-colors">{t(`${label(g.path)}.name`)}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{t(`${label(g.path)}.desc`)}</p>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export const BotApps = () => {
  const { t } = useTranslation('landing');
  return (
    <section className="py-24 bg-gradient-to-b from-[#0f0f12] to-[#0a0a0b]">
      <div className="max-w-6xl mx-auto px-4">
        <Head kicker={t('home.apps.kicker')} title={t('home.apps.title')} subtitle={t('home.apps.subtitle')} />
        <div className="grid grid-cols-4 lg:grid-cols-8 gap-3">
          {APP_GUIDES.map((g) => (
            <Link key={g.path} to={g.path} title={t(`${label(g.path)}.name`)} className="group flex flex-col items-center gap-2 p-4 rounded-2xl border border-gray-800 bg-white/[0.03] hover:border-amber-500/40 transition-colors">
              <span className="w-12 h-12 rounded-xl bg-white p-2 flex items-center justify-center">
                <img src={g.logo} alt="" className="w-full h-full object-contain" loading="lazy" />
              </span>
              <span className="text-xs text-gray-300 group-hover:text-white text-center">{t(`${label(g.path)}.short`)}</span>
            </Link>
          ))}
        </div>
        <div className="mt-10 grid md:grid-cols-2 gap-4">
          <Link to="/ai-bots/connected-apps" className="group p-6 rounded-2xl bg-white/5 border border-gray-800 hover:border-amber-500/40 transition-colors">
            <h3 className="text-white font-semibold mb-2 flex items-center gap-2"><Plug className="w-4 h-4 text-amber-400" />{t('home.apps.mcp_title')}</h3>
            <p className="text-gray-400 text-sm leading-relaxed">{t('home.apps.mcp_desc')}</p>
          </Link>
          <Link to="/integrations/mcp" className="group p-6 rounded-2xl bg-white/5 border border-gray-800 hover:border-amber-500/40 transition-colors">
            <h3 className="text-white font-semibold mb-2 flex items-center gap-2"><Bot className="w-4 h-4 text-amber-400" />{t('home.apps.server_title')}</h3>
            <p className="text-gray-400 text-sm leading-relaxed">{t('home.apps.server_desc')}</p>
          </Link>
        </div>
        <p className="text-center text-sm text-gray-500 mt-6">{t('home.apps.note')}</p>
      </div>
    </section>
  );
};

// The browser, the desktop app and the phone apps: one account everywhere. The apps' cards
// use their pages' link labels (guide_links.*); the browser card leads to sign-up.
const DEVICES = [
  { path: '/register', Icon: Globe, labelKey: 'home.devices.web' },
  ...DOWNLOAD_GUIDES.map((g) => ({ path: g.path, Icon: ICONS[g.icon] || Monitor, labelKey: label(g.path) })),
];

export const Devices = () => {
  const { t } = useTranslation('landing');
  return (
    <section className="py-24 bg-[#0a0a0b]">
      <div className="max-w-6xl mx-auto px-4">
        <Head kicker={t('home.devices.kicker')} title={t('home.devices.title')} subtitle={t('home.devices.subtitle')} />
        <div className="grid md:grid-cols-3 gap-4">
          {DEVICES.map(({ path, Icon, labelKey }) => (
            <Link key={path} to={path} className="group p-6 rounded-2xl bg-white/5 border border-gray-800 hover:border-amber-500/40 transition-colors">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center mb-4">
                <Icon className="w-5 h-5 text-amber-400" />
              </div>
              <h3 className="text-white font-semibold mb-2 group-hover:text-amber-400 transition-colors">{t(`${labelKey}.name`)}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{t(`${labelKey}.desc`)}</p>
            </Link>
          ))}
        </div>
        <div className="text-center mt-8">
          <Link to="/download" className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 font-medium">
            {t('home.devices.more')}<ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

// Prices come from backend/config/plans.json (usePlanCatalog), in the visitor's currency; the
// points match its numbers (1 October 2026).
const PLANS = [
  { key: 'free', points: ['work_free', 'bots_1', 'credits_30', 'models_fast'] },
  { key: 'basic', points: ['work_unlimited', 'bots_3', 'credits_380', 'models_standard'] },
  { key: 'pro', points: ['bots_10', 'credits_1150', 'computer', 'schedules_3'], highlight: true },
  { key: 'max', points: ['bots_unlimited', 'credits_4000', 'models_premium', 'schedules_20'] },
];

export const PlansTeaser = () => {
  const { t, i18n } = useTranslation('landing');
  const { user } = useAuth();
  const currency = displayCurrency(user);
  const catalog = usePlanCatalog(currency);
  const money = (id) => formatMoney(catalog.find((p) => p.id === id)?.priceMonthly ?? 0, currency, i18n.language);
  return (
    <section className="py-24 bg-[#0a0a0b]">
      <div className="max-w-6xl mx-auto px-4">
        <Head kicker={t('home.plans.kicker')} title={t('home.plans.title')} subtitle={t('home.plans.subtitle')} />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PLANS.map((p) => (
            <div key={p.key} className={`p-6 rounded-2xl border ${p.highlight ? 'border-amber-500/50 bg-amber-500/[0.06]' : 'border-gray-800 bg-white/[0.03]'}`}>
              <h3 className="text-white font-semibold text-lg">{t(`home.plans.names.${p.key}`)}</h3>
              <div className="mt-2 mb-5">
                <span className="text-3xl font-bold text-white">{money(p.key)}</span>
                <span className="text-gray-500 text-sm"> {t('home.plans.per_month')}</span>
              </div>
              <ul className="space-y-2">
                {p.points.map((point) => (
                  <li key={point} className="flex items-start gap-2 text-sm text-gray-300">
                    <Check className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />{t(`home.plans.points.${point}`)}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="text-center text-sm text-gray-500 mt-6">{t('home.plans.note', { team: money('team') })}</p>
        <div className="text-center mt-6">
          <Link to="/pricing" className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 font-medium">
            {t('home.plans.cta')}<ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export const CompareStrip = () => {
  const { t } = useTranslation('landing');
  return (
    <section className="py-20 bg-gradient-to-b from-[#0a0a0b] to-[#0f0f12]">
      <div className="max-w-6xl mx-auto px-4">
        <Head kicker={t('home.compare.kicker')} title={t('home.compare.title')} subtitle={t('home.compare.subtitle')} />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {COMPARE_GUIDES.map((g) => (
            <Link key={g.path} to={g.path} className="group p-5 rounded-xl bg-white/5 border border-gray-800 hover:border-amber-500/40 transition-colors">
              <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">{t('alternatives.index.vs_label')}</div>
              <div className="text-white font-semibold mb-1 group-hover:text-amber-400 transition-colors">{t(`${label(g.path)}.rival`)}</div>
              <p className="text-gray-400 text-sm leading-relaxed">{t(`${label(g.path)}.desc`)}</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};
